export type CampaignTemplateId =
  | "announcement"
  | "promo"
  | "newsletter"
  | "feature"
  | "simple";

export type CampaignContentFields = {
  templateId: CampaignTemplateId;
  eyebrow: string;
  headline: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  heroImageUrl: string;
  images: string[];
};

export const CAMPAIGN_TEMPLATES: Array<{
  id: CampaignTemplateId;
  label: string;
  description: string;
  defaults: Omit<CampaignContentFields, "templateId" | "heroImageUrl" | "images">;
}> = [
  {
    id: "announcement",
    label: "Announcement",
    description: "News, updates, and product launches",
    defaults: {
      eyebrow: "Announcement",
      headline: "Something new on Sqaleshop",
      body: "Hi {{name}},\n\nWe have an update we think you’ll like. Here’s what’s new and how it helps your store grow.",
      ctaLabel: "Open dashboard",
      ctaUrl: "https://sqaleshop.com/dashboard",
    },
  },
  {
    id: "promo",
    label: "Promo",
    description: "Offers, discounts, and seasonal campaigns",
    defaults: {
      eyebrow: "Limited offer",
      headline: "A special offer for you",
      body: "Hi {{name}},\n\nFor a short time, you can take advantage of this offer. Add an image above and keep the message short.",
      ctaLabel: "Claim offer",
      ctaUrl: "https://sqaleshop.com",
    },
  },
  {
    id: "newsletter",
    label: "Newsletter",
    description: "Tips, recap, and monthly roundup",
    defaults: {
      eyebrow: "Newsletter",
      headline: "This month with Sqaleshop",
      body: "Hi {{name}},\n\nA few things worth knowing this month:\n\n• What merchants are shipping more of\n• A quick tip to improve your store\n• What’s coming next",
      ctaLabel: "Read more",
      ctaUrl: "https://sqaleshop.com",
    },
  },
  {
    id: "feature",
    label: "New feature",
    description: "Walk through a product capability",
    defaults: {
      eyebrow: "New",
      headline: "A better way to sell",
      body: "Hi {{name}},\n\nWe just shipped a feature to help you sell faster. Drop in a screenshot, explain the benefit, and point people to try it.",
      ctaLabel: "Try it now",
      ctaUrl: "https://sqaleshop.com/dashboard",
    },
  },
  {
    id: "simple",
    label: "Simple note",
    description: "Plain message with optional image",
    defaults: {
      eyebrow: "",
      headline: "A note from Sqaleshop",
      body: "Hi {{name}},\n\nWrite your message here. Add images if you need them — they’ll show up in the email automatically.",
      ctaLabel: "",
      ctaUrl: "",
    },
  },
];

const P = "margin:0 0 14px;font-size:15px;color:#292d32;line-height:1.6;";
const H1 =
  "margin:0 0 14px;font-family:Inter,Helvetica,Arial,sans-serif;font-size:26px;font-weight:800;letter-spacing:-0.02em;line-height:1.2;color:#451892;";
const EYEBROW =
  "display:inline-block;margin:0 0 12px;padding:6px 12px;border-radius:999px;background-color:#f3ecff;color:#451892;font-family:Inter,Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;";
const BTN =
  "display:inline-block;background-color:#451892;color:#ffffff;font-family:Inter,Helvetica,Arial,sans-serif;font-size:14px;font-weight:700;text-decoration:none;padding:14px 28px;border-radius:999px;";
const IMG =
  "width:100%;max-width:536px;height:auto;border-radius:18px;margin:0 0 20px;display:block;border:0;outline:none;";

export const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const looksLikeHtml = (value: string) => /<[a-z][\s\S]*>/i.test(value.trim());

export const textToEmailHtml = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return "";
  if (looksLikeHtml(trimmed)) return trimmed;
  return trimmed
    .split(/\n{2,}/)
    .map(
      (paragraph) =>
        `<p style="${P}">${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`
    )
    .join("");
};

const imageBlock = (url: string, alt = "") =>
  `<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" width="536" style="${IMG}" />`;

export const buildCampaignBodyHtml = (fields: CampaignContentFields) => {
  const parts: string[] = [];

  if (fields.eyebrow.trim()) {
    parts.push(`<span class="eyebrow" style="${EYEBROW}">${escapeHtml(fields.eyebrow.trim())}</span>`);
  }
  if (fields.headline.trim()) {
    parts.push(`<h1 style="${H1}">${escapeHtml(fields.headline.trim())}</h1>`);
  }
  if (fields.heroImageUrl) {
    parts.push(imageBlock(fields.heroImageUrl, fields.headline || "Campaign image"));
  }

  const bodyHtml = textToEmailHtml(fields.body);
  if (bodyHtml) parts.push(bodyHtml);

  fields.images.filter(Boolean).forEach((url, index) => {
    parts.push(imageBlock(url, `Image ${index + 1}`));
  });

  if (fields.ctaLabel.trim() && fields.ctaUrl.trim()) {
    parts.push(
      `<div style="text-align:center;margin:28px 0 8px;"><a href="${escapeHtml(fields.ctaUrl.trim())}" class="btn" style="${BTN}">${escapeHtml(fields.ctaLabel.trim())}</a></div>`
    );
  }

  return parts.join("\n");
};

export const emptyCampaignFields = (
  templateId: CampaignTemplateId = "announcement"
): CampaignContentFields => {
  const preset = CAMPAIGN_TEMPLATES.find((item) => item.id === templateId) ?? CAMPAIGN_TEMPLATES[0];
  return {
    templateId,
    ...preset.defaults,
    heroImageUrl: "",
    images: [],
  };
};

export const wrapBrandedEmailHtml = (
  bodyHtml: string,
  options: { title?: string; preheader?: string; logoSrc?: string } = {}
) => {
  const title = escapeHtml(options.title || "Sqaleshop");
  const preheader = escapeHtml(options.preheader || "A message from Sqaleshop");
  const logoSrc = options.logoSrc || "/email/site_logo_dark.png";
  const year = new Date().getFullYear();

  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
</head>
<body style="margin:0;padding:0;width:100%;background-color:#f6f4ef;font-family:Manrope,Helvetica,Arial,sans-serif;color:#292d32;">
  <div style="display:none;max-height:0;overflow:hidden;">${preheader}</div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f6f4ef;width:100%;">
    <tr>
      <td align="center" style="padding:32px 16px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;">
          <tr>
            <td style="background-color:#ffffff;border-radius:28px;overflow:hidden;box-shadow:0 24px 56px -28px rgba(69,24,146,0.28);">
              <div style="height:6px;background:linear-gradient(90deg,#451892 0%,#be9afc 100%);font-size:0;line-height:0;">&nbsp;</div>
              <div style="padding:36px 32px 28px;">
                <div style="text-align:center;padding-bottom:22px;margin-bottom:8px;border-bottom:1px solid #ebe6f5;">
                  <img src="${logoSrc}" alt="Sqaleshop" width="160" height="32" style="height:32px;width:auto;max-width:180px;margin:0 auto;display:block;border:0;" />
                </div>
                ${bodyHtml}
              </div>
              <div style="background-color:#451892;color:#ffffff;text-align:center;padding:16px 24px;font-size:12px;">
                <span style="color:#be9afc;">●</span>&nbsp;
                Powered by <strong>Sqaleshop</strong>
              </div>
            </td>
          </tr>
          <tr>
            <td style="text-align:center;padding:22px 16px 8px;">
              <p style="margin:0 0 6px;color:#6b7280;font-size:12px;">© ${year} Sqaleshop. All rights reserved.</p>
              <p style="margin:0;color:#9ca3af;font-size:11px;">You received this email because you have a Sqaleshop account.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};
